import React, { useState } from "react";
import {
  Box,
  Typography,
  Paper,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import {
  FieldValues,
  FormContainer,
  TextFieldElement,
  useForm,
} from "react-hook-form-mui";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useTranslation } from "react-i18next";
import InfoMessage from "src/shared/components/InfoMessage";
import { MessageVariant } from "src/shared/enums/messageTypes/messageTypes";
import { useLocation } from "react-router-dom";
import useResetPassword from "src/welcome/data-access/useResetPassword";
import { Visibility, VisibilityOff } from "@mui/icons-material";

const schema = () => {
  const { t } = useTranslation();
  return yup.object().shape({
    newPassword: yup
      .string()
      .min(7, t("Το πεδίο πρέπει να αποτελείται από τουλάχιστον 7 χαρακτήρες"))
      .required(t("Το πεδίο είναι υποχρεωτικό")),
    confirmPassword: yup
      .string()
      .oneOf([yup.ref("newPassword")], t("Οι κωδικοί δεν ταιριάζουν"))
      .required(t("Το πεδίο είναι υποχρεωτικό")),
  });
};

export default function ResetPasswordForm() {
  const { t } = useTranslation();
  const validationSchema = schema();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const location = useLocation();
  const formContext = useForm({
    resolver: yupResolver(validationSchema),
  });

  const { mutate: resetPassword, isLoading } = useResetPassword();

  const onSubmit = (data: FieldValues) => {
    resetPassword(data);
  };

  return (
    <Box
      sx={{
        width: "80vw",
        mx: "auto",
        display: "flex",
        flexDirection: "column",
        gap: 3,
      }}
    >
      <InfoMessage
        variant={MessageVariant.Info}
        message={
          <Box sx={{ p: 1 }}>
            <Typography variant="body2">
              {t("Το όνομα χρήστη / Username σας είναι το")}{" "}
              <Typography component="span" fontWeight="bold">
                {location.state?.m01UserName}
              </Typography>
              {t(
                '. Αν θέλετε να το αλλάξετε μπορείτε να το κάνετε μετά την είσοδό σας στην εφαρμογή, από την επιλογή "Στοιχεία χρήστη και πρόσβασης" στο επάνω δεξιά μέρος του "προφίλ χρήστη".',
              )}
            </Typography>
          </Box>
        }
      />
      <Paper
        elevation={1}
        sx={{ p: "1rem", display: "flex", flexDirection: "column", gap: 1 }}
      >
        <Typography variant="h6" gutterBottom>
          {t("Ορισμός Νέου Κωδικού για το όνομα χρήστη")}{" "}
          <Typography component="span" fontWeight="bold">
            {location.state?.m01UserName}
          </Typography>{" "}
          {t("που αφορά το Σύστημα της Εξωστρέφειας του ΓΕΜΗ")}
        </Typography>
        <FormContainer formContext={formContext} onSuccess={onSubmit}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "start",
              gap: 2,
              mb: 3,
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1,
                width: "80%",
              }}
            >
              <Typography>{t("Νέο Password")}</Typography>
              <TextFieldElement
                name="newPassword"
                type={showPassword ? "text" : "password"}
                id="new-password"
                placeholder={t("τουλάχιστον 7 χαρακτήρες")}
                required
                fullWidth
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword((prev) => !prev)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1,
                width: "80%",
              }}
            >
              <Typography>{t("Νέο Password (επιβεβαίωση)")}</Typography>
              <TextFieldElement
                name="confirmPassword"
                type={showConfirmPassword ? "text" : "password"}
                id="confirm-password"
                placeholder={t("επαναλάβετε")}
                required
                fullWidth
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowConfirmPassword((prev) => !prev)}
                        edge="end"
                      >
                        {showConfirmPassword ? (
                          <VisibilityOff />
                        ) : (
                          <Visibility />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          </Box>
          <Box>
            <LoadingButton
              variant="contained"
              type="submit"
              loading={isLoading}
            >
              {t("Αποθήκευση")}
            </LoadingButton>
          </Box>
        </FormContainer>
      </Paper>
    </Box>
  );
}
